// user.selectors.ts (maintains backward compatibility)

import { createFeatureSelector, createSelector } from '@ngrx/store';
import { UserState } from './auth.reducer';

// Feature selector - keep the same name as before
export const selectUserState = createFeatureSelector<UserState>('user');

// Basic selectors - maintain exact same names
export const selectToken = createSelector(
  selectUserState,
  (state) => state.token
);

export const selectLearmoProfile = createSelector(
  selectUserState,
  (state) => state.learmoProfile
);

export const selectAppwriteProfile = createSelector(
  selectUserState,
  (state) => state.appwriteProfile
);

// Keep these for backward compatibility
export const selectIsAuthenticated = createSelector(
  selectToken,
  selectLearmoProfile,
  (token, profile) => !!token && !!profile
);

export const selectIsLoading = createSelector(
  selectUserState,
  (state) => state.profileUpdateProcess?.status === 'loading'
);

export const selectAuthError = createSelector(
  selectUserState,
  (state) => state.error
);

// User details selectors
export const selectUserRole = createSelector(
  selectLearmoProfile,
  (profile) => profile?.role?.name
);

export const selectUserSubscription = createSelector(
  selectLearmoProfile,
  (profile) => profile?.subscription
);

export const selectIsSubscribed = createSelector(
  selectUserSubscription,
  (subscription) => subscription?.isSubscribed ?? false
);

export const selectUserAvatar = createSelector(
  selectLearmoProfile,
  (profile) => profile?.avatar
);

export const selectUserEmail = createSelector(
  selectLearmoProfile,
  (profile) => profile?.email
);

export const selectUserName = createSelector(
  selectLearmoProfile,
  (profile) =>
    profile ? `${profile.firstName} ${profile.lastName}` : null
);

// Registration selectors
export const selectIsRegistering = createSelector(
  selectUserState,
  (state) => state.isRegistering
);

export const selectIsWebhookComplete = createSelector(
  selectUserState,
  (state) => state.isRegistrationWebhookDone
);

// Token management selectors
export const selectIsTokenRefreshScheduled = createSelector(
  selectUserState,
  (state) => state.isTokenRefreshScheduled
);

export const selectTokenScheduledTimeout = createSelector(
  selectUserState,
  (state) => state.tokenTimeout
);

// Process status selectors
export const selectProfileUpdateProcess = createSelector(
  selectUserState,
  (state) => state.profileUpdateProcess
);

export const selectEmailUpdateProcess = createSelector(
  selectUserState,
  (state) => state.emailUpdateProcess
);

export const selectIsEmailChanged = createSelector(
  selectUserState,
  (state) => state.isEmailChanged
);

export const selectPasswordUpdateProcess = createSelector(
  selectUserState,
  (state) => state.passwordUpdateProcess
);

export const selectEnable2FaUpdateProcess = createSelector(
  selectUserState,
  (state) => state.enable2FaUpdateProcess
);

export const selectLoginMethodUpdateProcess = createSelector(
  selectUserState,
  (state) => state.loginMethodUpdateProcess
);

export const selectPasswordRecoveryProcess = createSelector(
  selectUserState,
  (state) => state.passwordRecoveryProcess
);

// Login methods selector
export const selectLoginMethods = createSelector(
  selectUserState,
  (state) => state.loginMethods
);

// Onboarding selectors
export const selectOnboardingStatus = createSelector(
  selectLearmoProfile,
  (profile) => profile?.onBoarding
);

export const selectIsWebsiteSetupDone = createSelector(
  selectOnboardingStatus,
  (onboarding) => onboarding?.websiteSetting ?? false
);

// Combined status selectors
export const selectIsProfileUpdateLoading = createSelector(
  selectProfileUpdateProcess,
  (process) => process?.status === 'loading'
);

export const selectProfileUpdateError = createSelector(
  selectProfileUpdateProcess,
  (process) => process?.error
);

// Composite selectors for UI components
export const selectAuthUIState = createSelector(
  selectIsAuthenticated,
  selectIsLoading,
  selectAuthError,
  selectLearmoProfile,
  selectAppwriteProfile,
  (isAuthenticated, isLoading, error, learmoProfile, appwriteProfile) => ({
    isAuthenticated,
    isLoading,
    error,
    user: {
      learmoProfile,
      appwriteProfile,
    },
  })
);

export const selectUserDashboardData = createSelector(
  selectLearmoProfile,
  selectUserRole,
  selectIsSubscribed,
  selectUserAvatar,
  (profile, role, isSubscribed, avatar) => ({
    profile,
    role,
    isSubscribed,
    avatar,
  })
);